import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, FileText, Plus, Users, Building2, ChevronRight,
  Shield, CheckCircle2, XCircle, Clock, Filter,
  GraduationCap, Calendar, AlertCircle, Bell, Inbox
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

import { API_URL } from '../utils/api';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  in_progress: { label: 'In Progress', color: 'bg-blue-50 text-blue-700' },
  review: { label: 'Review', color: 'bg-amber-50 text-amber-700' },
  finalized: { label: 'Finalized', color: 'bg-green-50 text-green-700' },
  archived: { label: 'Archived', color: 'bg-gray-50 text-gray-500' },
};

const DOC_STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  finalized: { label: 'Finalized', color: 'bg-green-50 text-green-700' },
  archived: { label: 'Archived', color: 'bg-gray-50 text-gray-500' },
};

const TYPE_CONFIG = {
  iep: { label: 'IEP', color: 'bg-[#3B6A9B]/10 text-[#3B6A9B]' },
  met: { label: 'MET', color: 'bg-[#5A8A5A]/10 text-[#5A8A5A]' },
};

const DOC_TYPE_CONFIG = {
  referral: { label: 'Referral', icon: Inbox, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  meeting_notice: { label: 'Meeting Notice', icon: Bell, color: 'bg-blue-50 text-blue-700 border-blue-200' },
};

export default function IEPMETProductPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [context, setContext] = useState(null);
  const [cases, setCases] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cases');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [docFilterType, setDocFilterType] = useState('all');
  const [docFilterStatus, setDocFilterStatus] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCase, setNewCase] = useState({ student_id: '', case_type: 'iep', case_subtype: 'initial', context_type: 'initial', due_date: '', meeting_date: '', notes: '' });
  const [docCreateOpen, setDocCreateOpen] = useState(false);
  const [docCreating, setDocCreating] = useState(false);
  const [newDoc, setNewDoc] = useState({ student_id: '', document_type: 'referral' });

  const fetchData = useCallback(async () => {
    try {
      const [ctxRes, casesRes, studentsRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/iep-met/context`, { withCredentials: true }).catch((err) => {
          console.warn('Context fetch failed', err?.response?.status);
          return { data: { _fetchError: true, access: { has_access: true } } };
        }),
        axios.get(`${API_URL}/api/iep-met/cases`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/students`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/documents`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setContext(ctxRes.data);
      setCases(Array.isArray(casesRes.data) ? casesRes.data : []);
      setStudents(studentsRes.data);
      setDocuments(Array.isArray(docsRes.data) ? docsRes.data : []);
    } catch (err) {
      console.error('Failed to fetch IEP/MET data', err);
      setContext({ _fetchError: true, access: { has_access: true } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!newCase.student_id) return;
    setCreating(true);
    try {
      const body = { student_id: newCase.student_id, case_type: newCase.case_type };
      if (newCase.case_subtype) body.case_subtype = newCase.case_subtype;
      if (newCase.case_type === 'iep' && newCase.context_type) body.context_type = newCase.context_type;
      if (newCase.due_date) body.due_date = newCase.due_date;
      if (newCase.meeting_date) body.meeting_date = newCase.meeting_date;
      if (newCase.notes) body.notes = newCase.notes;
      const res = await axios.post(`${API_URL}/api/iep-met/cases`, body, { withCredentials: true });
      const caseId = res.data.case_id;
      const subtype = newCase.case_subtype;

      // Auto carry-forward for annual IEP, addendum IEP, and reevaluation MET
      if (['annual', 'addendum', 'reevaluation'].includes(subtype)) {
        try {
          await axios.post(`${API_URL}/api/iep-met/cases/${caseId}/carry-forward`, {}, { withCredentials: true });
        } catch (cfErr) {
          console.warn('Carry-forward skipped:', cfErr.response?.data?.reason || cfErr.message);
        }
      }

      setCreateOpen(false);
      setNewCase({ student_id: '', case_type: 'iep', case_subtype: 'initial', context_type: 'initial', due_date: '', meeting_date: '', notes: '' });
      navigate(`/products/iep-met/cases/${caseId}`);
    } catch (err) {
      console.error('Failed to create case', err);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateDoc = async () => {
    if (!newDoc.student_id) return;
    setDocCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/documents`, {
        student_id: newDoc.student_id,
        document_type: newDoc.document_type,
      }, { withCredentials: true });
      setDocCreateOpen(false);
      setNewDoc({ student_id: '', document_type: 'referral' });
      navigate(`/products/iep-met/documents/${res.data.document_id}`);
    } catch (err) {
      console.error('Failed to create document', err);
    } finally {
      setDocCreating(false);
    }
  };

  const filtered = cases.filter(c => {
    if (filterType !== 'all' && c.case_type !== filterType) return false;
    if (filterStatus !== 'all' && c.status !== filterStatus) return false;
    return true;
  });

  const filteredDocs = documents.filter(d => {
    if (docFilterType !== 'all' && d.document_type !== docFilterType) return false;
    if (docFilterStatus !== 'all' && d.status !== docFilterStatus) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#3B6A9B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hasAccess = context?.access?.has_access;
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-6">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 max-w-md text-center">
          <XCircle className="h-10 w-10 text-red-400 mx-auto mb-4" />
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C] mb-2">Access Required</h2>
          <p className="text-sm text-[#595959] mb-6">Your organization does not have access to Frame IEP/MET.</p>
          <Link to="/dashboard"><Button variant="outline" className="rounded-xl" data-testid="no-access-back">Back to Dashboard</Button></Link>
        </div>
      </div>
    );
  }

  const counts = context?.counts || {};

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E5E5]">
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="flex items-center gap-2 text-[#595959] hover:text-[#1C1C1C] transition-colors" data-testid="back-to-dashboard-btn">
                <ArrowLeft className="h-5 w-5" />
                <span className="text-sm font-medium hidden sm:inline">FrameEd</span>
              </Link>
              <div className="h-6 w-px bg-[#E5E5E5]" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#3B6A9B]/10">
                  <FileText className="h-4 w-4 text-[#3B6A9B]" />
                </div>
                <span className="font-outfit font-semibold text-[#1C1C1C]">Frame IEP/MET</span>
              </div>
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" data-testid="new-case-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Case
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-outfit">Create New Case</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label>Student</Label>
                    <Select value={newCase.student_id} onValueChange={v => setNewCase(p => ({ ...p, student_id: v }))}>
                      <SelectTrigger className="rounded-xl mt-1" data-testid="case-student-select">
                        <SelectValue placeholder="Select a student" />
                      </SelectTrigger>
                      <SelectContent>
                        {students.map(s => (
                          <SelectItem key={s.student_id} value={s.student_id}>
                            {s.first_name} {s.last_name} {s.grade ? `(${s.grade})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Case Type</Label>
                    <Select value={newCase.case_type} onValueChange={v => setNewCase(p => ({ ...p, case_type: v }))}>
                      <SelectTrigger className="rounded-xl mt-1" data-testid="case-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="iep">IEP (Individualized Education Program)</SelectItem>
                        <SelectItem value="met">MET (Multidisciplinary Evaluation Team)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newCase.case_type === 'iep' && (
                    <div>
                      <Label>Case Subtype</Label>
                      <Select value={newCase.case_subtype} onValueChange={v => setNewCase(p => ({ ...p, case_subtype: v, context_type: v }))}>
                        <SelectTrigger className="rounded-xl mt-1" data-testid="case-subtype-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="initial">Initial (first-time eligibility)</SelectItem>
                          <SelectItem value="annual">Annual Review</SelectItem>
                          <SelectItem value="reevaluation">Reevaluation</SelectItem>
                          <SelectItem value="transfer">Transfer</SelectItem>
                          <SelectItem value="addendum">Addendum</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {newCase.case_type === 'met' && (
                    <div>
                      <Label>Case Subtype</Label>
                      <Select value={newCase.case_subtype} onValueChange={v => setNewCase(p => ({ ...p, case_subtype: v }))}>
                        <SelectTrigger className="rounded-xl mt-1" data-testid="case-subtype-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="initial">Initial Evaluation</SelectItem>
                          <SelectItem value="reevaluation">Reevaluation</SelectItem>
                          <SelectItem value="transfer">Transfer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Due Date</Label>
                      <Input type="date" className="rounded-xl mt-1" value={newCase.due_date} onChange={e => setNewCase(p => ({ ...p, due_date: e.target.value }))} data-testid="case-due-date" />
                    </div>
                    <div>
                      <Label>Meeting Date</Label>
                      <Input type="date" className="rounded-xl mt-1" value={newCase.meeting_date} onChange={e => setNewCase(p => ({ ...p, meeting_date: e.target.value }))} data-testid="case-meeting-date" />
                    </div>
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Textarea className="rounded-xl mt-1" rows={2} value={newCase.notes} onChange={e => setNewCase(p => ({ ...p, notes: e.target.value }))} data-testid="case-notes" />
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild><Button variant="outline" className="rounded-xl">Cancel</Button></DialogClose>
                  <Button className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" onClick={handleCreate} disabled={!newCase.student_id || creating} data-testid="create-case-submit">
                    {creating ? 'Creating...' : 'Create Case'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {/* New Document Dialog */}
            <Dialog open={docCreateOpen} onOpenChange={setDocCreateOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-xl border-[#E5E5E5] text-[#595959] hover:text-[#1C1C1C]" data-testid="new-document-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Document
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-outfit">Create New Document</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label>Student</Label>
                    <Select value={newDoc.student_id} onValueChange={v => setNewDoc(p => ({ ...p, student_id: v }))}>
                      <SelectTrigger className="rounded-xl mt-1" data-testid="doc-student-select">
                        <SelectValue placeholder="Select a student" />
                      </SelectTrigger>
                      <SelectContent>
                        {students.map(s => (
                          <SelectItem key={s.student_id} value={s.student_id}>
                            {s.first_name} {s.last_name} {s.grade ? `(${s.grade})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Document Type</Label>
                    <Select value={newDoc.document_type} onValueChange={v => setNewDoc(p => ({ ...p, document_type: v }))}>
                      <SelectTrigger className="rounded-xl mt-1" data-testid="doc-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="referral">Referral</SelectItem>
                        <SelectItem value="meeting_notice">Meeting Notice</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className={`rounded-xl border p-3 text-sm ${newDoc.document_type === 'referral' ? 'bg-amber-50/50 border-amber-200 text-amber-800' : 'bg-blue-50/50 border-blue-200 text-blue-800'}`}>
                    {newDoc.document_type === 'referral'
                      ? 'A Referral documents the initial request for special education evaluation, including reason, areas of concern, and interventions tried.'
                      : 'A Meeting Notice notifies parents/guardians and team members of an upcoming IEP or evaluation meeting, including date, purpose, and participants.'}
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild><Button variant="outline" className="rounded-xl">Cancel</Button></DialogClose>
                  <Button className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" onClick={handleCreateDoc} disabled={!newDoc.student_id || docCreating} data-testid="create-doc-submit">
                    {docCreating ? 'Creating...' : 'Create Document'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="stat-total-cases">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-5 w-5 text-[#3B6A9B]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[#595959]">Total Cases</span>
            </div>
            <p className="font-outfit text-2xl font-semibold text-[#1C1C1C]">{counts.total_cases || 0}</p>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="stat-iep-cases">
            <div className="flex items-center gap-2 mb-2">
              <GraduationCap className="h-5 w-5 text-[#3B6A9B]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[#595959]">IEP Cases</span>
            </div>
            <p className="font-outfit text-2xl font-semibold text-[#1C1C1C]">{counts.iep_cases || 0}</p>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="stat-met-cases">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-[#5A8A5A]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[#595959]">MET Cases</span>
            </div>
            <p className="font-outfit text-2xl font-semibold text-[#1C1C1C]">{counts.met_cases || 0}</p>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="stat-documents">
            <div className="flex items-center gap-2 mb-2">
              <Inbox className="h-5 w-5 text-amber-600" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[#595959]">Documents</span>
            </div>
            <p className="font-outfit text-2xl font-semibold text-[#1C1C1C]">{documents.length}</p>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex items-center gap-1 mb-6 border-b border-[#E5E5E5]" data-testid="product-tabs">
          <button onClick={() => setActiveTab('cases')}
            className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${activeTab === 'cases' ? 'text-[#3B6A9B]' : 'text-[#595959] hover:text-[#1C1C1C]'}`}
            data-testid="tab-cases">
            Cases
            {activeTab === 'cases' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#3B6A9B] rounded-full" />}
          </button>
          <button onClick={() => setActiveTab('documents')}
            className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${activeTab === 'documents' ? 'text-[#3B6A9B]' : 'text-[#595959] hover:text-[#1C1C1C]'}`}
            data-testid="tab-documents">
            Documents
            <Badge className="ml-1.5 text-[10px] px-1.5 py-0 bg-gray-100 text-gray-600">{documents.length}</Badge>
            {activeTab === 'documents' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#3B6A9B] rounded-full" />}
          </button>
        </div>

        {/* Cases Tab */}
        {activeTab === 'cases' && (
          <>
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-[#595959]" />
                <span className="text-sm font-medium text-[#595959]">Filter:</span>
              </div>
              <div className="flex gap-2">
                {['all', 'iep', 'met'].map(t => (
                  <button key={t} onClick={() => setFilterType(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterType === t ? 'bg-[#3B6A9B] text-white' : 'bg-[#F3F4F1] text-[#595959] hover:bg-[#E5E5E5]'}`}
                    data-testid={`filter-type-${t}`}
                  >
                    {t === 'all' ? 'All Types' : t.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className="h-4 w-px bg-[#E5E5E5]" />
              <div className="flex gap-2 flex-wrap">
                {['all', 'draft', 'in_progress', 'review', 'finalized'].map(s => (
                  <button key={s} onClick={() => setFilterStatus(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus === s ? 'bg-[#3B6A9B] text-white' : 'bg-[#F3F4F1] text-[#595959] hover:bg-[#E5E5E5]'}`}
                    data-testid={`filter-status-${s}`}
                  >
                    {s === 'all' ? 'All Status' : STATUS_CONFIG[s]?.label || s}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-12 text-center" data-testid="empty-cases">
                <FileText className="h-12 w-12 text-[#E5E5E5] mx-auto mb-4" />
                <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C] mb-2">
                  {cases.length === 0 ? 'No cases yet' : 'No cases match filters'}
                </h3>
                <p className="text-sm text-[#595959] mb-6">
                  {cases.length === 0 ? 'Create your first IEP or MET case to get started.' : 'Try adjusting your filters.'}
                </p>
                {cases.length === 0 && (
                  <Button className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" onClick={() => setCreateOpen(true)} data-testid="empty-create-btn">
                    <Plus className="h-4 w-4 mr-2" /> Create First Case
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map(c => {
                  const typeConf = TYPE_CONFIG[c.case_type] || TYPE_CONFIG.iep;
                  const statusConf = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft;
                  const student = c.student || {};
                  const prog = c.section_progress || { total: 0, complete: 0 };
                  const progressPct = prog.total > 0 ? Math.round((prog.complete / prog.total) * 100) : 0;

                  return (
                    <div key={c.case_id}
                      onClick={() => navigate(`/products/iep-met/cases/${c.case_id}`)}
                      className="block bg-white border border-[#E5E5E5] rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer"
                      data-testid={`case-card-${c.case_id}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-xl bg-[#3B6A9B]/10 flex items-center justify-center shrink-0">
                            <FileText className="h-5 w-5 text-[#3B6A9B]" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-outfit font-semibold text-[#1C1C1C] truncate">{c.title}</h3>
                              <Badge className={`${typeConf.color} text-xs shrink-0`}>{typeConf.label}</Badge>
                              {(c.case_subtype || c.context_type) && <Badge className="bg-purple-50 text-purple-700 text-xs shrink-0 capitalize">{c.case_subtype || c.context_type}</Badge>}
                              <Badge className={`${statusConf.color} text-xs shrink-0`}>{statusConf.label}</Badge>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-[#595959]">
                              <Link to={`/products/iep-met/students/${c.student_id}/timeline`}
                                onClick={e => e.stopPropagation()}
                                className="flex items-center gap-1 hover:text-[#3B6A9B] transition-colors"
                                data-testid={`case-timeline-${c.case_id}`}>
                                <Users className="h-3 w-3" />
                                {student.first_name} {student.last_name}
                              </Link>
                              {c.due_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  Due: {c.due_date}
                                </span>
                              )}
                              {c.meeting_date && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  Meeting: {c.meeting_date}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <div className="hidden sm:flex items-center gap-2 w-32">
                            <div className="flex-1 h-1.5 bg-[#F3F4F1] rounded-full overflow-hidden">
                              <div className="h-full bg-[#3B6A9B] rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                            </div>
                            <span className="text-xs text-[#595959] whitespace-nowrap">{prog.complete}/{prog.total}</span>
                          </div>
                          <ChevronRight className="h-5 w-5 text-[#E5E5E5]" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <>
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-[#595959]" />
                <span className="text-sm font-medium text-[#595959]">Filter:</span>
              </div>
              <div className="flex gap-2">
                {['all', 'referral', 'meeting_notice'].map(t => (
                  <button key={t} onClick={() => setDocFilterType(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${docFilterType === t ? 'bg-[#3B6A9B] text-white' : 'bg-[#F3F4F1] text-[#595959] hover:bg-[#E5E5E5]'}`}
                    data-testid={`doc-filter-type-${t}`}
                  >
                    {t === 'all' ? 'All Types' : DOC_TYPE_CONFIG[t]?.label || t}
                  </button>
                ))}
              </div>
              <div className="h-4 w-px bg-[#E5E5E5]" />
              <div className="flex gap-2">
                {['all', 'draft', 'finalized', 'archived'].map(s => (
                  <button key={s} onClick={() => setDocFilterStatus(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${docFilterStatus === s ? 'bg-[#3B6A9B] text-white' : 'bg-[#F3F4F1] text-[#595959] hover:bg-[#E5E5E5]'}`}
                    data-testid={`doc-filter-status-${s}`}
                  >
                    {s === 'all' ? 'All Status' : DOC_STATUS_CONFIG[s]?.label || s}
                  </button>
                ))}
              </div>
            </div>

            {filteredDocs.length === 0 ? (
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-12 text-center" data-testid="empty-documents">
                <Inbox className="h-12 w-12 text-[#E5E5E5] mx-auto mb-4" />
                <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C] mb-2">
                  {documents.length === 0 ? 'No documents yet' : 'No documents match filters'}
                </h3>
                <p className="text-sm text-[#595959] mb-6">
                  {documents.length === 0 ? 'Create a Referral or Meeting Notice to get started.' : 'Try adjusting your filters.'}
                </p>
                {documents.length === 0 && (
                  <Button className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" onClick={() => setDocCreateOpen(true)} data-testid="empty-create-doc-btn">
                    <Plus className="h-4 w-4 mr-2" /> Create First Document
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredDocs.map(d => {
                  const dtConf = DOC_TYPE_CONFIG[d.document_type] || DOC_TYPE_CONFIG.referral;
                  const dsConf = DOC_STATUS_CONFIG[d.status] || DOC_STATUS_CONFIG.draft;
                  const DocIcon = dtConf.icon;
                  const student = d.student || {};

                  return (
                    <div key={d.document_id}
                      onClick={() => navigate(`/products/iep-met/documents/${d.document_id}`)}
                      className="block bg-white border border-[#E5E5E5] rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer"
                      data-testid={`doc-card-${d.document_id}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${d.document_type === 'referral' ? 'bg-amber-50' : 'bg-blue-50'}`}>
                            <DocIcon className={`h-5 w-5 ${d.document_type === 'referral' ? 'text-amber-600' : 'text-blue-600'}`} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-outfit font-semibold text-[#1C1C1C] truncate">{d.title}</h3>
                              <Badge className={`${dtConf.color} text-xs shrink-0 border`}>{dtConf.label}</Badge>
                              <Badge className={`${dsConf.color} text-xs shrink-0`}>{dsConf.label}</Badge>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-[#595959]">
                              <Link to={`/products/iep-met/students/${d.student_id}/timeline`}
                                onClick={e => e.stopPropagation()}
                                className="flex items-center gap-1 hover:text-[#3B6A9B] transition-colors"
                                data-testid={`doc-timeline-${d.document_id}`}>
                                <Users className="h-3 w-3" />
                                {student.first_name} {student.last_name}
                              </Link>
                              {d.created_at && (
                                <span>Created: {new Date(d.created_at).toLocaleDateString()}</span>
                              )}
                              {d.linked_case_ids?.length > 0 && (
                                <span className="flex items-center gap-1 text-[#3B6A9B]">
                                  <AlertCircle className="h-3 w-3" />
                                  Linked to {d.linked_case_ids.length} case{d.linked_case_ids.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-[#E5E5E5] shrink-0" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
